import FHIRFetcher from 'data/FHIRFetcher';
import SendGridPoster from 'data/SendGridPoster';
import type { Bundle, BundleEntry, Patient, PlanDefinitionAction, Questionnaire } from 'fhir/r4';
import { EMAIL_TEMPLATES, TEXT_MESSAGE_TEMPLATES } from 'fhir-sdoh/common/constants';
import * as fhirpath from 'fhirpath';
import PatientCDO from 'models/patient';
import type { PatientCDOModel } from 'models/patient';
import moment from 'moment';
import { useEffect } from 'react';
import React from 'react';
import { List, Label, Loader, Popup, Confirm, Icon, Form } from 'semantic-ui-react';
import type { DropdownItemProps, DropdownProps } from 'semantic-ui-react';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';
import type { Fetched } from 'types';


import { useAuthContext } from '../../../../../contexts/AuthContext';
import { Assess, FHIRProxyClient } from '../../../../../fhir-sdoh';
// `../../../../types/flat-fhir` resolved to `components/types/flat-fhir`, which
// does not exist; the module is `src/types/flat-fhir`, five levels up.
import type { FlatCarePlan, FlatTask } from '../../../../../types/flat-fhir';
import type { FlatPatient } from '../../../../../types/patient-data';
import * as FHIRUtils from '../../../../../utils/FHIRUtils';
import type {
  StepCarePlanContext,
  StepConditionFlat,
  StepMutate,
  StepPatientIdentity,
  StepTasks,
} from '../../types';


/**
 * The planned-intervention action node selected on the assess step, i.e. a
 * PlanDefinition action returned by the intervene helpers.
 */
type PlannedInterventionAction = {
  id?: string;
  title?: string;
};

/**
 * Props for {@link Assessment}, as passed by `careplan/steps/index.tsx`.
 *
 * `condition` is the FLATTENED view here, unlike `Screening`, which receives the
 * raw FHIR `Condition`. See `careplan/types.ts`.
 */
type AssessmentProps = StepPatientIdentity &
  StepCarePlanContext & {
    /** The flattened screening condition; only `verificationStatusCode` is read. */
    condition?: StepConditionFlat;
    /** The `/Task/?patient=...` bundle's `entry` array, scanned for the assessment task. */
    tasks?: StepTasks;
    /** The org's questionnaire bundle, searched for the intake questionnaire. */
    questionnaireData?: Bundle | undefined;
    /** Receives the chosen intervention action so the stepper can unlock Intervene. */
    onAssess: (plannedInterventionAction: { id: string }) => void;
    /**
     * Opens the survey sidebar for the intake questionnaire.
     *
     * Optional to match the hubs it is forwarded through, but invoked unguarded
     * from the survey label's `onClick` below.
     */
    toggleSurveySidebar?: ((questionnaireId: string) => void) | undefined;
    /** Revalidates the email/SMS communication log. */
    communicationsMutate: StepMutate;
  };

function Assessment(props: AssessmentProps) {
  const [loading, setLoading] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [plannedInterventionAction, setPlannedInterventionAction] = React.useState<PlannedInterventionAction | null>(null);
  const [popOpen, setpopOpen] = React.useState(false);
  // SUSPECTED DEFECT — this state is SEEDED with one shape and WRITTEN with
  // another. The seed `PatientCDO` is a `PatientCDOModel`, whose `id` is a
  // required `string` and whose name/contact fields are `string[]`; the write in
  // `onSuccess` stores a `FlatPatient`, whose `id` is `string | undefined` and
  // whose corresponding fields are `unknown[]`. The two are not assignable
  // either way.
  //
  // Both sides' assumptions: the seed promises every consumer a fully populated
  // record with `''`/`['']` placeholders; `parseFHIRPatient` guarantees only what
  // FHIRPath found, so `flatPatient.id` is genuinely absent for a Patient with no
  // `id`. `planInternvention` below reads `flatPatient.id` and `.email` and posts
  // them to SendGrid, so before the `/Patient?_id=` fetch resolves it sends the
  // seed's empty strings rather than failing.
  //
  // Typed as the union of the two shapes because that is what the binding
  // actually holds; reported, not reconciled, because collapsing to either one
  // changes what a consumer receives.
  const [flatPatient, setFlatPatient] = React.useState<PatientCDOModel | FlatPatient>(PatientCDO);
  const [availableSurvey, setAvailableSurvey] = React.useState(false);

  const authInfo = useAuthContext();
  // Annotated `SWRConfiguration` because a detached options `const` infers
  // nothing from `useSWR` in swr 0.5.7 — the generic is solved from the inline
  // argument only.
  //
  // SUSPECTED DEFECT — `revalidateIfStale` is NOT an option in swr 0.5.7. Its
  // `Configuration` declares only `revalidateOnFocus`, `revalidateOnReconnect`
  // and `shouldRetryOnError` (see the installed `swr/dist/types.d.ts`);
  // `revalidateIfStale` arrived in swr 1.0. So this key is read by nothing and
  // the intended "do not refetch a stale cache entry on mount" never takes
  // effect — SWR revalidates on mount regardless.
  //
  // This is app-wide, not local: 43 occurrences across 40 files under
  // `apps/care-betty/src`. Reported, not fixed: removing the key is inert, but
  // the fix that matches the intent is an SWR upgrade, which is a behaviour
  // change. The extra key is admitted by widening the annotation, not by a cast.
  //
  // `Data` is `Fetched<Bundle>` rather than `Bundle`: `FHIRFetcher` resolves to
  // `undefined` without calling the API when the endpoint is empty, so the
  // fetcher's own return type carries the `undefined` and swr 0.5.7's tuple
  // overload only matches when the options agree with it.
  const options: SWRConfiguration<Fetched<Bundle>> & { revalidateIfStale: boolean } = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    onSuccess: (data: Fetched<Bundle>, _key: string, _config: unknown) => {
      // `data!.entry![0]!` preserves the existing throw on an empty
      // `/Patient?_id=` result; `as Patient` is the single-type search's claim.
      setFlatPatient(FHIRUtils.parseFHIRPatient(data!.entry![0]!.resource as Patient));
    },
  };
  const {
    data: _patientData,
    error: _patientError,
    mutate: _patientMutate,
  } = useSWR<Fetched<Bundle>, Error>([`/Patient?_id=` + props.patientId, authInfo.token], FHIRFetcher, options);

  const handlePopOpen = () => {
    setpopOpen(true);
  };
  const handleClose = () => {
    setpopOpen(false);
  };

  //const handleClick = () => setConfirmOpen(true);
  const closeConfirm = () => setConfirmOpen(false);
  // The Dropdown below sets each option's `value` to the whole PlanDefinition
  // action OBJECT, but semantic-ui-react types `DropdownProps['value']` as
  // `string | number | boolean | (string | number | boolean)[] | undefined`.
  // The two genuinely do not overlap, and that non-overlap is the finding: the
  // component stores back exactly what it put in the option, so the double
  // assertion records the round trip rather than blessing an unchecked shape.
  const handleSelect = (_e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    setPlannedInterventionAction(value as unknown as PlannedInterventionAction);
  };

  // params: payload: pl, care-hooks endpoint: hook, error message: em
  // `async` with no `await`, as upstream wrote it: the two call sites `await` the
  // returned promise, so dropping `async` changes what they observe.
  // eslint-disable-next-line @typescript-eslint/require-await
  async function handleSendGridPoster(pl: Record<string, unknown>, hook: string, em: string) {
    SendGridPoster(`/hooks/${hook}`, authInfo.token, pl).then(
      () => {
        setLoading(false);
        props.communicationsMutate();
      },
      (e) => {
        setLoading(false);
        console.log(`Error: Failed to send ${em}(s): ${(e as Error).toString()}`);
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async function handleSubmit() {
    setpopOpen(false);
    setConfirmOpen(true);
  }

  const carePlan = props.carePlan;
  let flatCarePlan: FlatCarePlan | undefined;
  if (carePlan) {
    flatCarePlan = FHIRUtils.parseFHIRCarePlan(carePlan);
  }

  // `getInterventionActions` returns `fhirpath.evaluate(...)`, declared
  // `unknown[]` by `src/fhirpath.d.ts` — the array-ness is real, the element
  // shape is a claim about the PlanDefinition's `intervention` action nodes,
  // which is what the Dropdown below reads `id`/`title` off.
  //
  // `planDefinition` is optional on the props while the helper requires it;
  // `fhirpath.evaluate(undefined, ...)` returns `[]` rather than throwing, so
  // the `!` states the existing reachability without changing the result.
  const interventionActions = Assess.getInterventionActions(
    props.planDefinition!
  ) as PlannedInterventionAction[];

  //get assessment tasks details if assessment has already been done
  let assessmentTaskId: string | undefined;
  let assessmentTask: FlatTask | undefined;
  const assessmentTaskRef: string = fhirpath
    .evaluate(props.carePlan, "activity.where(reference.display='Assessment').reference.reference")
    .toString();

  if (assessmentTaskRef) {
    assessmentTaskId = assessmentTaskRef.substring(assessmentTaskRef.indexOf('/') + 1);
    //Get Assessment Task to get who updated it and when
    if (props.tasks) {
      for (let i = 0; i < props.tasks.length; i++) {
        // `!` rather than a guard: `i` is bounded by `props.tasks.length`, and
        // `noUncheckedIndexedAccess` widens the read regardless.
        const task = props.tasks[i]!.resource;
        if (task.id === assessmentTaskId) {
          assessmentTask = FHIRUtils.parseFHIRTask(task);
          break;
        }
      }
    }
  }

  //get intervention (for completed assessments)
  const intervention = fhirpath
    .evaluate(
      props.carePlan,
      "identifier.where(system = 'https://projectwell.io/fhir/identifiers/selected-intervention').value"
    )
    .toString();
  let interventionTaskId: string | undefined;
  const interventionTaskRef: string = fhirpath
    .evaluate(props.carePlan, "activity.where(reference.display='Intervention').reference.reference")
    .toString();
  if (interventionTaskRef) {
    interventionTaskId = interventionTaskRef.substring(interventionTaskRef.indexOf('/') + 1);
  }

  //get intake questionnaire from planDef
  const intakeQuestionnaire = fhirpath
    .evaluate(props.planDefinition, "action.where(id = 'assessment').definitionCanonical")
    .toString();
  const intakeQuestionnaireId = intakeQuestionnaire.substring(intakeQuestionnaire.indexOf('/') + 1);

  useEffect(() => {
    // Narrowed to `Questionnaire` for the same reason as in the screen step:
    // only that member of the `FhirResource` union carries `item`, and the
    // `Object.keys(...).includes('item')` below is this code's own check for it.
    const questionnaire = (props.questionnaireData?.entry?.filter(
      (questionnaire: BundleEntry) => questionnaire?.resource?.id === intakeQuestionnaireId
    )[0] &&
      props.questionnaireData?.entry?.filter(
        (questionnaire: BundleEntry) => questionnaire?.resource?.id === intakeQuestionnaireId
      )?.[0]?.resource) as Questionnaire | undefined;
    if (questionnaire && Object.keys(questionnaire)?.includes('item') && questionnaire?.item?.length !== 0) {
      setAvailableSurvey(true);
    } else {
      setAvailableSurvey(false);
    }
  }, [intakeQuestionnaireId, props.questionnaireData]);

  //plan Intervention function
  const planInternvention = async () => {
    setConfirmOpen(false);
    setLoading(true);

    let payload: Record<string, unknown>;
    if (props.orgId === 'ZING') {
      payload = {
        to: flatPatient.email,
        from: 'nourishedrx@nourishedrx.com',
        patientId: flatPatient.id,
        templateId: EMAIL_TEMPLATES['ZING AFTER INTAKE EMAIL'],
        subject: 'ZING AFTER INTAKE EMAIL',
        sender: authInfo.practitionerName + ' (Program Enroll)',
        dynamicTemplateData: {
          memberName: flatPatient.firstName,
        },
        text: 'assess launch',
      };
      await handleSendGridPoster(payload, 'mail', 'email');
      payload = {
        // SUSPECTED DEFECT — REPORTED, NOT FIXED. Both phone fields are the raw fhirpath
        // COLLECTIONS, so this concatenates an array: one entry works by accident, two
        // produce "+15551234,5559999". Same shape as KNOWN_BUGS.md Q-11 and CP-27. Cast,
        // not indexed — picking `[0]` would change which number is used.
        phoneNumber: flatPatient.mobilePhone
          ? '+1' + (flatPatient.mobilePhone as unknown as string)
          : '+1' + (flatPatient.homePhone as unknown as string),
        patientId: flatPatient.id,
        message: TEXT_MESSAGE_TEMPLATES['ZING AFTER INTAKE EMAIL'],
        subject: 'ZING AFTER INTAKE TEXT',
        sender: authInfo.practitionerName + ' (Program Enroll)',
        text: 'assess launch',
      };
      await handleSendGridPoster(payload, 'text', 'text message');
    } else if (props.orgId === 'IU') {
      payload = {
        to: flatPatient.email,
        from: 'nourishedrx@nourishedrx.com',
        patientId: flatPatient.id,
        templateId: EMAIL_TEMPLATES['IU ENROLLMENT WELCOME EMAIL'],
        subject: 'ENROLLMENT WELCOME EMAIL',
        sender: authInfo.practitionerName + ' (Program Enroll)',
        dynamicTemplateData: {
          memberName: flatPatient.firstName,
        },
        text: 'assess launch',
      };
      await SendGridPoster(`/hooks/mail`, authInfo.token, payload).then(
        () => {
          setLoading(false);
          props.communicationsMutate();
        },
        (e) => {
          setLoading(false);
          console.log(`Error: Failed to send web survey email(s): ${(e as Error).toString()}`);
        }
      );
    }

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    const interventionType = fhirpath
      .evaluate(props.planDefinition, "action.where(id='intervention').code.first().text")
      .toString();

    // SUSPECTED DEFECT — `interventionTaskId` is assigned only when the CarePlan
    // carries an `activity.where(reference.display='Intervention')`, so it is
    // genuinely `undefined` for a plan whose intervention activity has not been
    // created yet. `planIntervention` declares that parameter as a required
    // `string` and forwards it to `CarePlanBuilder.buildSubTasks`, which builds
    // `partOfRef = 'Task/' + parentTaskId` — so an absent id is written into the
    // FHIR Task as the literal reference `"Task/undefined"` rather than failing.
    //
    // Both sides' assumptions: this caller believes the intervention task always
    // exists by the time an assessment is submitted; `buildSubTasks` guarantees
    // only string concatenation, and FHIR accepts the malformed reference.
    //
    // Reported, not fixed: guarding here would skip creating the sub-tasks
    // entirely, which is a different behaviour from writing a dangling `partOf`.
    // The `!`s state the reachability this code already assumes; `flatCarePlan`
    // is likewise unguarded today and `flatCarePlan.id` already throws when the
    // plan is absent.
    try {
      await Assess.planIntervention(
        props.orgId!,
        interventionType,
        plannedInterventionAction as unknown as PlanDefinitionAction,
        props.patientId!,
        flatCarePlan!.id,
        flatCarePlan!.careTeamId,
        interventionTaskId!,
        moment().format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD'),
        {
          reference: 'Practitioner/' + authInfo.practitionerId,
          display: authInfo.practitionerName,
        },
        authInfo.practitionerName,
        fhirProxyClient
      );

      // Delaying intentionally to process all backend tasks
      setTimeout(() => {
        // `onAssess` in `steps/index.tsx` reads `.id` off this argument
        // unguarded, while the selected action is `null` until the Dropdown has
        // been used and its `id` is optional. Reaching here means the Confirm
        // was accepted, which requires a selection.
        props.onAssess(plannedInterventionAction as { id: string }); //callback
        setLoading(false);
      }, 7000);
    } catch (error) {
      console.log('Error in Planning Intervention', (error instanceof Error ? error.message : String(error)));
      setLoading(false);
      throw error;
    }
  };

  return (
    <>
      {loading && <Loader active inline="centered" />}
      <List horizontal>
        {intervention && assessmentTask && (
          <>
            <Label color="orange">
              Enrolled
              <Label.Detail>
                <Icon name="group"></Icon>
              </Label.Detail>
            </Label>
            <Popup
              flowing
              trigger={
                <Label color="grey" image>
                  {moment(assessmentTask.lastUpdated).fromNow()}
                </Label>
              }
              content={
                <Label>
                  into {intervention} program By: {assessmentTask.lastModifiedBy}.{' '}
                  {moment(assessmentTask.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}
                </Label>
              }
              on="hover"
            />
          </>
        )}
        {!intervention && (
          <>
            <Label
              color={props.condition?.verificationStatusCode === 'confirmed' ? 'blue' : 'grey'}
              image
              className={availableSurvey ? '' : 'ui disabled button'}
            >
              <Icon name="file" />
              <Label.Detail as="a" onClick={() => props.toggleSurveySidebar!(intakeQuestionnaireId)}>
                {availableSurvey ? 'Take Survey' : 'No Available Survey'}
              </Label.Detail>
            </Label>
            <Label color={props.condition?.verificationStatusCode === 'confirmed' ? 'blue' : 'grey'} image>
              <Icon name="balance scale" />
              <Popup
                flowing
                position="top right"
                trigger={
                  <Label.Detail color={props.carePlan ? 'blue' : 'grey'} as="a">
                    Complete Assessment
                  </Label.Detail>
                }
                content={
                  <Form onSubmit={handleSubmit}>
                    <Form.Group>
                      <Form.Dropdown
                        placeholder="Select Recommended Program"
                        selection
                        // The producing half of the object-valued Dropdown
                        // documented at `handleSelect`: each option's `value` is
                        // the whole action node, which `DropdownItemProps`
                        // types as a primitive.
                        options={
                          interventionActions.map((interventionAction) => {
                            return {
                              key: interventionAction.id,
                              text: interventionAction.title,
                              value: interventionAction,
                            };
                          }) as unknown as DropdownItemProps[]
                        }
                        onChange={handleSelect}
                      />
                      <Form.Button primary content="Enroll" />
                    </Form.Group>
                  </Form>
                }
                on="click"
                open={popOpen}
                onOpen={handlePopOpen}
                onClose={handleClose}
              />
            </Label>

            <Confirm
              header={`Planned Intervention: ${plannedInterventionAction?.title}!`}
              open={confirmOpen}
              onCancel={closeConfirm}
              onConfirm={planInternvention}
            />
          </>
        )}
      </List>
    </>
  );
}

export default Assessment;
